/**
 * Eid coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_ramadan_eid`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const eidColoringPagesPack: ToilePack = {
    slug: 'eid-coloring-pages',
    category: 'season',
    name: 'Eid coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'lettering', label: 'Eid Mubarak', subject: 'the words "Eid Mubarak" in large hollow bubble letters under a crescent and stars, lanterns hanging at the sides'},
        {key: 'gifts', label: 'Gifts and sweets', subject: 'a table with wrapped gifts, a plate of sweets and a teapot beside a small lantern, bunting strung above'},
        {key: 'sheep', label: 'Eid al-Adha sheep', subject: 'a friendly sheep with curly wool standing on grass before a mosque dome and a crescent moon'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and simple', style: 'drawn for little ones in thick uniform outlines, few parts and big shapes', palette: 'black ink only', framing: 'the drawing centered on the page with a white margin'},
        {key: 'detailed', label: 'Detailed', style: 'drawn for older children in a thinner even line, with patterned wrapping, tiles and small floral details', palette: 'black ink only', framing: 'the drawing centered on the page with a white margin'},
        {key: 'doodle', label: 'Doodle fill', style: 'drawn in a fine even line with every large shape divided into small doodle patterns of dots, stripes and tiny flowers', palette: 'black ink only', framing: 'the drawing centered on the page with a white margin'},
    ],
    directions: ['a family', 'eid al-fitr', 'for toddlers'],
    directionHint: 'try: a family',
    seo: {
        title: 'Eid coloring pages: Eid Mubarak, gifts, Eid al-Adha sheep',
        metaDescription: 'Nine Eid coloring pages in three line ages: hollow Eid Mubarak lettering, wrapped gifts and sweets, and a sheep for Eid al-Adha.',
        h1: 'Eid coloring pages: Eid Mubarak, gifts and sweets, Eid al-Adha sheep',
        tagline: 'Nine line drawings of three Eid scenes, each in a bold, a detailed and a doodle-fill style.',
        intro: "Clean line drawings to colour on the days of Eid, at home or in the mosque's kids corner. One picture is the words Eid Mubarak in large hollow letters under a crescent. One is a table of wrapped gifts, sweets and a teapot. One is a friendly sheep on grass before a mosque dome, for Eid al-Adha. The bold style suits little children, the detailed style older ones, and doodle fill gives every shape a pattern.",
        useCases: [
            'a colouring page for the morning of Eid',
            'a mosque kids corner table',
            'a sheet for an Eid party',
            'a page for a visit to relatives',
        ],
        faq: [
            {q: 'Which picture is for Eid al-Adha?', a: 'The sheep, drawn on grass before a mosque dome and a crescent moon.'},
            {q: 'What is doodle fill?', a: 'Every large shape is divided into small patterns of dots, stripes and tiny flowers, to colour in one by one.'},
            {q: 'Are the pages ready to print?', a: 'Each is black ink on a white page with an even margin, ready to print and colour.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type a direction such as a family and the same line style draws it. That is a request, not a promised result.'},
        ],
    },
}
